import React, { useEffect, useRef, useState } from 'react';
import { Alert, App as AntdApp, Button, InputNumber, Progress, Segmented, Select, Space } from 'antd';

import { getLayoutForm, attachLayoutForm, removeLayoutForm, setLayoutFormPaper } from '../../services/cardLayouts';
import { FORM_FILE_TYPES, MAX_FORM_BYTES, MAX_FORM_MEGABYTES, formSummary, hasForm, paperPatch, sheetOrientation } from './pageLayoutForm';
import { PAGE_SIZES } from './pageLayoutModel';

/**
 * The page designer's Form section and, on a layout that has a form, its Paper
 * section (KPR-1860). Upload, replace, remove, and an image's paper size.
 *
 * Every action here writes to the server at once and is not an undo step. The
 * rules are KPR-1859's and each one is a place a layout could be lost:
 * - The layout must exist, and the server must hold what the person sees, so
 *   `ensureSaved` runs BEFORE an action starts. It is the one save near an
 *   upload: while an action runs `onBusy` holds the editor's Save, because a save
 *   during the render would lose to the upload's pin and the upload would be
 *   taken back.
 * - 409 `stale_layout` is never retried: the layout changed, so the person is
 *   offered Reload and looks at what changed. So is an answer that never came:
 *   the server may hold the form all the same.
 * - A refusal says the server's own sentence and changes nothing here.
 * - A PDF's paper is the PDF's: the size controls are not drawn. An image's go
 *   to `PATCH …/form`, never into the working copy, so a plain Save cannot
 *   confirm a paper nobody chose.
 */

// What a person reads when the server's answer has no sentence of its own.
const NOT_FINISHED = 'That did not finish. Check your connection and try again.';
const NOT_AVAILABLE = 'That layout is not available. It may have been deleted, or belong to someone else.';

function sentenceOf(error) {
	const answer = error?.response;
	if (!answer) return error?.sentence || NOT_FINISHED;
	if (answer.data?.code) return answer.data.message || NOT_FINISHED;
	if (answer.status === 404) return NOT_AVAILABLE;
	return answer.data?.message || NOT_FINISHED;
}

const sizeOptions = [...Object.keys(PAGE_SIZES).map(key => ({ value: key, label: key })), { value: 'custom', label: 'custom' }];

/** An image form's paper: the sheet's own controls, sent to `PATCH …/form` as they are chosen. */
function ImagePaper({ page, form, disabled, onPaper }) {
	const [custom, setCustom] = useState(null);
	const unset = form.paper === 'unset';
	// A size is chosen only when the image or the person has said one.
	const size = custom ? 'custom' : (unset ? undefined : page.size);
	const choose = (value) => {
		if (value === 'custom') {
			setCustom({ width: page.width, height: page.height });
			return;
		}
		setCustom(null);
		onPaper(paperPatch(page, { size: value }));
	};
	return (
		<>
			<Select
				value={size}
				onChange={choose}
				aria-label="Page size"
				placeholder="Choose a paper size"
				disabled={disabled}
				options={sizeOptions}
			/>
			<Segmented
				block
				aria-label="Page orientation"
				value={sheetOrientation(page)}
				disabled={disabled || unset}
				onChange={orientation => onPaper(paperPatch(page, { orientation }))}
				options={[{ value: 'portrait', label: 'Portrait' }, { value: 'landscape', label: 'Landscape' }]}
			/>
			{custom ? (
				<Space wrap>
					<InputNumber
						value={custom.width}
						onChange={width => setCustom(last => ({ ...last, width }))}
						aria-label="Page width"
						step={0.25}
						min={0.1}
						disabled={disabled}
					/>
					<InputNumber
						value={custom.height}
						onChange={height => setCustom(last => ({ ...last, height }))}
						aria-label="Page height"
						step={0.25}
						min={0.1}
						disabled={disabled}
					/>
					<span className="page-editor__unit">{page.unit}</span>
					<Button
						disabled={disabled || !custom.width || !custom.height}
						onClick={() => {
							onPaper(paperPatch(page, { size: 'custom', width: custom.width, height: custom.height }));
							setCustom(null);
						}}
					>
						Set size
					</Button>
				</Space>
			) : null}
		</>
	);
}

export default function PageFormPanel({
	cardId,
	layoutId = null,
	collectionId = null,
	body,
	unsaved = false,
	holding = false,
	ensureSaved,
	onBusy,
	onLanded,
	onReload,
}) {
	const { modal } = AntdApp.useApp();
	const fileInput = useRef(null);
	const form = body.form || null;
	const onForm = hasForm(body);

	// The action in flight: { kind, percent }, percent null while the server draws.
	const [task, setTask] = useState(null);
	const [refusal, setRefusal] = useState(null);
	const [note, setNote] = useState(null);
	const [stale, setStale] = useState(false);
	const [scan, setScan] = useState(null);
	const busy = Boolean(task) || holding;

	// A flagged form is told and never a block: what prints is keepr's own image.
	const fileId = form?.fileId || null;
	useEffect(() => {
		setScan(null);
		if (!fileId || !layoutId) return undefined;
		let active = true;
		getLayoutForm(cardId, layoutId, { collectionId })
			.then(answer => { if (active) setScan(answer?.scan || null); })
			.catch(() => {}); // The word is a courtesy: the form itself reads elsewhere.
		return () => { active = false; };
	}, [cardId, layoutId, collectionId, fileId]);

	const run = async (kind, act) => {
		setTask({ kind, percent: kind === 'upload' ? 0 : null });
		setRefusal(null);
		setNote(null);
		setStale(false);
		onBusy?.(true);
		try {
			const id = await ensureSaved();
			if (!id) return; // A question the person must answer first (the save showed it).
			const answer = await act(id);
			onLanded?.(answer.layout);
			setNote(answer.changes?.message || null);
		} catch (error) {
			setRefusal(sentenceOf(error));
			setStale(error?.response?.data?.code === 'stale_layout' || (!error?.response && !error?.sentence));
		} finally {
			setTask(null);
			onBusy?.(false);
		}
	};

	const onFile = (event) => {
		const file = event.target.files?.[0];
		// The same file again is a choice too.
		event.target.value = '';
		if (!file) return;
		if (file.size > MAX_FORM_BYTES) {
			setRefusal(`A form can be at most ${MAX_FORM_MEGABYTES} MB.`);
			setNote(null);
			setStale(false);
			return;
		}
		run('upload', id => attachLayoutForm(cardId, id, file, {
			onProgress: (fraction) => {
				// Every byte sent: what is left is the server drawing the pages.
				const percent = fraction >= 1 ? null : Math.round(fraction * 100);
				setTask(last => (last ? { ...last, percent } : last));
			},
		}));
	};

	const confirmRemove = () => modal.confirm({
		title: 'Remove this form?',
		content: 'Every block stays where it is, and blocks from later pages move onto the one sheet that is left.',
		okText: 'Remove form',
		okButtonProps: { danger: true },
		onOk: () => { run('remove', id => removeLayoutForm(cardId, id)); },
	});

	const setPaper = paper => run('paper', id => setLayoutFormPaper(cardId, id, paper));

	return (
		<>
			<div className="micro-label page-editor__side-heading">Form</div>
			{onForm ? (
				<div className="page-form__file">
					<span className="page-form__name">{form.name}</span>
					<span className="page-form__meta">{formSummary(body)}</span>
				</div>
			) : (
				<p className="form-panel__hint">
					Upload a form you already use, a PDF, PNG or JPEG, and place your fields on it. Up to {MAX_FORM_MEGABYTES} MB.
				</p>
			)}
			<input
				ref={fileInput}
				type="file"
				hidden
				accept={FORM_FILE_TYPES}
				onChange={onFile}
				data-testid="page-form-input"
			/>
			<Space wrap className="page-form__actions">
				<Button disabled={busy} onClick={() => fileInput.current?.click()}>{onForm ? 'Replace form' : 'Upload form'}</Button>
				{onForm ? <Button danger disabled={busy} onClick={confirmRemove}>Remove form</Button> : null}
			</Space>
			{unsaved && !task ? <p className="form-panel__hint">Your changes are saved first.</p> : null}
			<div role="status" aria-live="polite">
				{task?.kind === 'upload' ? (
					<div className="page-form__progress">
						<Progress percent={task.percent ?? 100} status="active" showInfo={false} size="small" aria-label="Upload progress" />
						<span className="page-form__meta">
							{task.percent === null ? 'Drawing the pages. This can take a moment.' : `Uploading, ${task.percent}%`}
						</span>
					</div>
				) : null}
				{task?.kind === 'remove' ? <span className="page-form__meta">Removing the form.</span> : null}
				{task?.kind === 'paper' ? <span className="page-form__meta">Setting the paper size.</span> : null}
			</div>
			{refusal ? (
				<Alert
					type="error"
					showIcon
					message={refusal}
					action={stale ? <Button size="small" onClick={onReload}>Reload</Button> : null}
				/>
			) : null}
			{note ? <Alert type="info" showIcon message={note} closable onClose={() => setNote(null)} /> : null}
			{scan === 'flagged' ? (
				<Alert type="warning" showIcon message="The malware scan flagged this file. What prints is keepr's own image of it, and the file itself is served to nobody." />
			) : null}

			{onForm ? (
				<>
					<div className="micro-label page-editor__side-heading">Paper</div>
					{form.type === 'pdf' ? (
						<p className="form-panel__hint">The paper is the form's own: {formSummary(body)}.</p>
					) : (
						<>
							{form.paper === 'unset' ? (
								<Alert type="warning" showIcon message="This image does not say how big it is. Choose its paper size." />
							) : null}
							<ImagePaper page={body.page} form={form} disabled={busy} onPaper={setPaper} />
						</>
					)}
				</>
			) : null}
		</>
	);
}
